import React, { useMemo, useRef, useState } from 'react';
import {
  createAssetProcessingDoc,
  downloadBlob,
  exportDomToPng,
  failAssetDoc,
  finalizeAssetDoc,
  uploadBlobToStorage,
} from './printablesExport';

const BUSINESS_CARD_LOGO_URL = '/d_logo-removebg-preview.png';

const WhatsAppDPPrintable = ({
  firebase,
  authUser,
  tenantId,
  branchId,
  profile,
  branch,
  brandKit,
  assignedEmail,
  assignedEmailMissing,
}) => {
  const dpRef = useRef(null);
  const [saveToAssets, setSaveToAssets] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [warning, setWarning] = useState('');

  const partnerLabel = useMemo(() => {
    const city = branch?.city || branch?.branchName || '';
    return city ? `Authorized Partner - ${city}` : 'Authorized Partner';
  }, [branch?.branchName, branch?.city]);

  const accent = brandKit?.colors?.accent || '#c8a951';
  const canSave = Boolean(tenantId && branchId && authUser?.uid);

  const handleExport = async () => {
    if (!dpRef.current) return;

    setExporting(true);
    setMessage('');
    setError('');
    setWarning('');

    let assetRef = null;
    try {
      const blob = await exportDomToPng({
        element: dpRef.current,
        backgroundColor: '#ffffff',
        scale: 3,
      });
      const fileName = `Douglas-McCartney-Profile-Picture-${(profile.fullName || 'franchisee')
        .replace(/[^a-zA-Z0-9-]+/g, '-')
        .toLowerCase()}.png`;
      downloadBlob(blob, fileName);

      if (saveToAssets) {
        if (!canSave) {
          setWarning('Upload failed, file not saved.');
        } else {
          try {
            const snapshot = {
              fullName: profile.fullName || '',
              designation: profile.designation || '',
              phone: profile.phone || '',
              assignedEmail: assignedEmail || '',
              branchName: branch.branchName || '',
              address: branch.address || '',
              city: branch.city || '',
              reraId: branch.reraId || '',
              brand: {
                colors: brandKit?.colors || {},
                disclaimerText: brandKit?.disclaimerText || '',
                logoUrl: BUSINESS_CARD_LOGO_URL,
              },
            };

            assetRef = await createAssetProcessingDoc({
              firebase,
              tenantId,
              branchId,
              authUserId: authUser?.uid || '',
              type: 'whatsapp_dp',
              snapshot,
            });

            const storagePath = `tenants/${tenantId}/assets/${assetRef.id}/whatsapp-dp.png`;
            const url = await uploadBlobToStorage({
              firebase,
              storagePath,
              blob,
              contentType: 'image/png',
              metadata: { tenantId, branchId, createdBy: authUser?.uid || '' },
            });

            await finalizeAssetDoc({
              firebase,
              assetRef,
              output: { pngUrl: url, storagePath },
            });
          } catch (uploadError) {
            console.error('Profile picture asset upload failed:', uploadError);
            setWarning('Upload failed, file not saved.');
            if (assetRef) {
              await failAssetDoc({ firebase, assetRef, errorMessage: uploadError.message });
            }
          }
        }
      }
      setMessage('Profile picture downloaded.');
    } catch (err) {
      console.error('Profile picture export failed:', err);
      setError('Could not generate PNG. Try again.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="printable-layout">
      <div className="printable-preview">
        <div
          className="printable-square"
          ref={dpRef}
          style={{ border: `1px solid ${accent}` }}
        >
          {BUSINESS_CARD_LOGO_URL ? (
            <img src={BUSINESS_CARD_LOGO_URL} alt="Logo" crossOrigin="anonymous" />
          ) : (
            <div className="printable-logo-text">
              <span>Douglas &amp;</span>
              <span>McCartney</span>
            </div>
          )}
          <div className="square-footer">{partnerLabel}</div>
        </div>
      </div>

      <div className="printable-panel">
        <div className="printable-status success">Ready</div>
        {assignedEmailMissing && (
          <div className="printable-banner warning">Assigned email not set yet.</div>
        )}
        {message && <div className="printable-banner info">{message}</div>}
        {warning && <div className="printable-banner warning">{warning}</div>}
        {error && <div className="printable-banner error">{error}</div>}

        <div className="printable-detail">
          <span>Branch</span>
          <strong>{branch.branchName || 'Douglas & McCartney'}</strong>
        </div>
        <div className="printable-detail">
          <span>Partner Line</span>
          <strong>{partnerLabel}</strong>
        </div>

        <div className="printable-actions">
          <label className="printable-checkbox">
            <input
              type="checkbox"
              checked={saveToAssets}
              onChange={(event) => setSaveToAssets(event.target.checked)}
            />
            Save to Assets Library
          </label>
          <button
            type="button"
            className="printable-action"
            disabled={exporting}
            onClick={handleExport}
          >
            {exporting ? 'Generating...' : 'Download PNG'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WhatsAppDPPrintable;
